/**
 * Scope Intelligence findings panel types (REST).
 *
 * Source: Backend-SSE-Integration-Guide §6
 */

export type FindingType =
  | 'scope_readiness'
  | 'readiness_finding'
  | 'key_insight'
  | 'contradiction'
  | 'assumption'
  | 'strategy_alignment';

/** Scope-level cards that always live in the panel, never inline under chat. */
export const PANEL_PINNED_FINDING_TYPES: ReadonlySet<FindingType> = new Set([
  'scope_readiness',
  'readiness_finding',
]);

export type FindingStatus =
  | 'unreviewed'
  | 'investigating'
  | 'confirmed'
  | 'dismissed'
  | 'validated'
  | 'untested'
  | 'resolved'
  | 'expired'
  | 'active';

export type FindingAction =
  | 'confirm'
  | 'dismiss'
  | 'investigate'
  | 'validate'
  | 'mark_untested'
  | 'restore';

export type FindingVerdictColor = 'green' | 'amber' | 'red';

export type FindingResolutionActor = 'user' | 'system' | 'write_back' | null;

export interface FindingSource {
  kind: string;
  ref: string;
  label: string;
}

/**
 * Loosely-typed per-finding metadata. Shape varies by finding_type (routing
 * flags for scope_readiness, `layer` for key_insight, the fuller contradiction
 * text under `content.summary`, etc.), so only the fields the UI reads are
 * declared; the rest is passed through.
 */
export type FindingSeverity =
  | 'low'
  | 'medium'
  | 'high'
  | 'blocker'
  | 'warning'
  | 'insight';

export interface FindingDetails {
  content?: { summary?: string } | null;
  severity?: FindingSeverity;
  confidence?: number;
  /** v3 readiness_finding body */
  explanation?: string;
  suggestion?: string;
  kind?: string;
  [key: string]: unknown;
}

export interface Finding {
  id: string;
  finding_type: FindingType;
  status: FindingStatus;
  content_summary: string;
  details?: FindingDetails | null;
  source: FindingSource;
  created_at: string;
  resolved_at: string | null;
  resolution_actor: FindingResolutionActor;
  applicable_actions: FindingAction[];
  turn_id: string | null;
  subject_ref: string | null;
}

export interface FindingsSummary {
  total: number;
  unreviewed: number;
  investigating: number;
  confirmed: number;
  dismissed: number;
}

export interface FindingsVerdict {
  color: FindingVerdictColor;
  status_line: string;
  reviewed_count: number;
  confirmed_key_insight_count?: number;
  investigating_contradiction_count?: number;
  untested_assumption_count?: number;
}

export interface FindingsPanelResponse {
  scope_id: string;
  findings: Finding[];
  summary: FindingsSummary;
  verdict: FindingsVerdict;
}

export interface ResolveFindingRequest {
  action: FindingAction;
  user_id: string;
}

export interface ResolveFindingResponse {
  finding_id: string;
  status: FindingStatus;
  finding_type: FindingType;
  resolution_actor: FindingResolutionActor;
  resolved_at: string | null;
  verdict: FindingsVerdict;
  /** Present when action is `investigate` — send as next chat user_message */
  investigate_message?: string;
}

/** Ephemeral in-turn finding annotation from SSE (display-only). */
export interface InTurnFindingFlash {
  id: string;
  type: 'contradiction' | 'assumption' | 'key_insight';
  content: string;
  severity?: 'low' | 'medium' | 'high';
  super_agent_id?: string;
  side_a?: string;
  side_b?: string;
  confidence?: number;
}
